import { useContext, useRef } from "react";

// helpers
import {
    ApiContext,
    CheckoutActionTypes,
    CheckoutContext,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_PENNY_TXN_CONFIRM_ORDER_STATE,
} from "@swiggy-private/checkout-core";
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import { PollingEnabledPaymentFlow } from "@payment-client/includes/PaymentFlow";

interface UsePennyTxnStatusPollingProps {
    pollingDurationInMs: number;
    pollingIntervalInMs: number;
    startTime?: number;
    paymentCode: string;
}

const usePennyTxnStatusPolling = ({
    pollingDurationInMs,
    pollingIntervalInMs,
    startTime,
    paymentCode,
}: UsePennyTxnStatusPollingProps) => {
    const isMountedRef = useMountedRef();
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const apiContextValue = useContext(ApiContext);
    const txnStatusRef = useRef(false);
    const paymentFlowRef = useRef<PollingEnabledPaymentFlow | null>(null);

    useEffectOnce(() => {
        if (checkoutState.type !== CheckoutActionTypes.PENNY_TXN_STATUS_POLLER || apiContextValue.host.isNone()) {
            return;
        }

        const paymentFlow = new PollingEnabledPaymentFlow(
            apiContextValue.host.unwrap(),
            null, // order data is not required for penny txn status polling
            checkoutState.payload.transaction,
        );
        paymentFlowRef.current = paymentFlow;

        const orderId = checkoutState.payload.orderId;
        const _startTime = startTime || Date.now();

        // (3.1) Penny drop polling start — previously uninstrumented.
        recordNREvent({
            eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
            state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.PENNY_TXN_POLLING_START,
            orderId,
            paymentCode,
            pollingDurationMs: pollingDurationInMs,
            pollingIntervalMs: pollingIntervalInMs,
            startTime: _startTime,
        });

        const moveToNextState = (): void => {
            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_TRANSACTION,
                payload: {
                    orderId: checkoutState.payload.orderId,
                    transaction: checkoutState.payload.transaction,
                    transactionReqData: checkoutState.payload.transactionReqData,
                    useJuspaySDK: checkoutState.payload.useJuspaySDK,
                },
            });
        };

        paymentFlow
            .checkTxnStatus({
                duration: pollingDurationInMs,
                delay: pollingIntervalInMs,
                startTime: _startTime,
                paymentCode: paymentCode,
            })
            .then((pollResult) => {
                if (!isMountedRef.current || !paymentFlow.isRunning()) {
                    return;
                }

                txnStatusRef.current = true;

                // (3.2) Penny drop polling resolved and the flow moved forward. pollResult=true means a
                // terminal txn status was found; false means the poll timed out and we fell through.
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
                    state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.PENNY_TXN_POLLING_SUCCESS,
                    orderId,
                    paymentCode,
                    pollResult: String(pollResult),
                    elapsedMs: Date.now() - _startTime,
                    visibilityState: document.visibilityState,
                });

                if (document.visibilityState == "hidden" || document.hidden) {
                    paymentFlow.confirmPennyTransaction();
                    window.requestAnimationFrame(moveToNextState);
                } else {
                    paymentFlow.stop();
                    moveToNextState();
                }
            });

        return (): void => {
            if (!txnStatusRef.current) {
                // (3.4) User navigated away before penny drop polling resolved.
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
                    state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.PENNY_TXN_POLLING_STOPPED,
                    orderId,
                    reason: "unmount_before_success",
                });
                paymentFlow.stop();
            }
        };
    });

    return (): void => paymentFlowRef.current?.stop();
};

export default usePennyTxnStatusPolling;
